import {Injectable} from "@angular/core";
import {StoryUserModel} from "./reports.objects";
import {UserModelService} from "../services/usermodel/usermodel.service";
import {CompletionState} from "../services/usermodel/usermodel.objects";
import {EventBus} from "../event/EventBus";
import {AudioQueueEvent, InterruptAudio} from "../event/AudioQueueEvent";
import {AudioQueueItem, TranslateAudio} from "../services/audio-playback/audioqueue.item";
import {AudioQueueService} from "../services/audio-playback/audioqueue.service";

@Injectable()
export class FluencyReportAudio {

    private report:StoryUserModel = <StoryUserModel>{recordingWCPMs: [], reviewWords: []};
    private goal: number;

    constructor(private eventBus: EventBus,
                private userModelService: UserModelService,
                private audioQueueService: AudioQueueService) {
    }

    private lastRecordingPassedGoal(): boolean {
        let lastRecord = this.report.recordingWCPMs[this.report.recordingWCPMs.length - 1];
        return lastRecord.wcpm >= this.goal;
    }

    private previousRecordingPassedGoal(): boolean {
        let lastRecord = this.report.recordingWCPMs[this.report.recordingWCPMs.length - 2];
        return lastRecord.wcpm >= this.goal;
    }

    private haveWordsToPractice(): boolean {
        return this.report.reviewWords.length > 0;
    }

    private playFirstRecordAudio(): void {
        if (this.lastRecordingPassedGoal() && this.haveWordsToPractice()) {
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new AudioQueueEvent([
                    new AudioQueueItem("RA_n99", TranslateAudio.YES),
                    new AudioQueueItem("RA_RPT_FLUENCY_07", TranslateAudio.YES),
                    new AudioQueueItem("RA_RPT_FLUENCY_08", TranslateAudio.YES),
                    new AudioQueueItem("RA_RPT_FLUENCY_09", TranslateAudio.YES)
                ], InterruptAudio.NO));
            });
        } else if (this.lastRecordingPassedGoal() && !this.haveWordsToPractice()) {
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new AudioQueueEvent([
                    new AudioQueueItem("RA_n99", TranslateAudio.YES),
                    new AudioQueueItem("RA_RPT_FLUENCY_07", TranslateAudio.YES),
                    new AudioQueueItem("RA_RPT_FLUENCY_08", TranslateAudio.YES),
                    new AudioQueueItem("RA_RPT_FLUENCY_10", TranslateAudio.YES)
                ], InterruptAudio.NO));
            });
        } else {
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new AudioQueueEvent([
                    new AudioQueueItem("RA_RPT_FLUENCY_04", TranslateAudio.YES),
                    new AudioQueueItem("RA_RPT_FLUENCY_05", TranslateAudio.YES),
                    new AudioQueueItem("RA_RPT_FLUENCY_06", TranslateAudio.YES)
                ], InterruptAudio.NO));
            });
        }
    }

    private playSecondRecordAudio(): void {
        let allowedToPass = this.lastRecordingPassedGoal() || this.previousRecordingPassedGoal();

        if (allowedToPass && this.haveWordsToPractice()) {
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new AudioQueueEvent([
                    new AudioQueueItem("RA_n99", TranslateAudio.YES),
                    new AudioQueueItem("RA_RPT_FLUENCY_12", TranslateAudio.YES)
                ], InterruptAudio.NO));
            });
        } else if (allowedToPass && !this.haveWordsToPractice()) {
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new AudioQueueEvent([
                    new AudioQueueItem("RA_n99", TranslateAudio.YES),
                    new AudioQueueItem("RA_RPT_FLUENCY_13", TranslateAudio.YES)
                ], InterruptAudio.NO));
            });
        } else {
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new AudioQueueEvent([
                    new AudioQueueItem("RA_RPT_FLUENCY_11", TranslateAudio.YES)
                ], InterruptAudio.NO));
            });
        }
    }

    private playThirdRecordAudio(): void {
        if (this.lastRecordingPassedGoal() && this.haveWordsToPractice()) {
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new AudioQueueEvent([
                    new AudioQueueItem("RA_n99", TranslateAudio.YES),
                    new AudioQueueItem("RA_RPT_FLUENCY_15", TranslateAudio.YES)
                ], InterruptAudio.NO));
            });
        } else if (this.lastRecordingPassedGoal() && !this.haveWordsToPractice()) {
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new AudioQueueEvent([
                    new AudioQueueItem("RA_n99", TranslateAudio.YES),
                    new AudioQueueItem("RA_RPT_FLUENCY_16", TranslateAudio.YES)
                ], InterruptAudio.NO));
            });
        } else {
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(new AudioQueueEvent([
                    new AudioQueueItem("RA_RPT_FLUENCY_14", TranslateAudio.YES)
                ], InterruptAudio.NO));
            });
        }
    }

    public playAppropriateAudio(report: StoryUserModel, goal:number): void {
        this.report = report;
        this.goal = goal;
        if (this.report.recordingWCPMs.length === 1) {
            this.playFirstRecordAudio();
        } else if (this.report.recordingWCPMs.length === 2) {
            this.playSecondRecordAudio();
        } else {
            this.playThirdRecordAudio();
        }
    }

    public playClosingAudio(): void {
        if (this.userModelService.recordCompletionState !== CompletionState.COMPLETED) {
            if (this.haveWordsToPractice()) {
                this.audioQueueService.reset().subscribe(() => {
                    this.eventBus.next(new AudioQueueEvent([
                        new AudioQueueItem("RA_RPT_FLUENCY_03", TranslateAudio.YES)
                    ], InterruptAudio.NO));
                });
            } else {
                this.audioQueueService.reset().subscribe(() => {
                    this.eventBus.next(new AudioQueueEvent([
                        new AudioQueueItem("RA_RPT_FLUENCY_02", TranslateAudio.YES)
                    ], InterruptAudio.NO));
                });
            }
        }
    }
}
